import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import apiClient from '../../utils/apiClient'
import AvitoAccountFormDialog from '../settings/integrations/AvitoAccountFormDialog'
import {
	AVITO_PROPERTY_RIGHTS,
	buildCreatedAccountPreviewSelection,
	buildAvitoPreviewParams,
	groupPreviewDiagnostics,
	mergePreviewAccountCandidates,
	prettyPreviewValue,
} from '../../utils/avitoPreview'

const codeBlockSx = {
	backgroundColor: 'action.hover',
	borderRadius: 1,
	fontFamily: 'monospace',
	fontSize: '0.8rem',
	margin: 0,
	maxHeight: 360,
	overflow: 'auto',
	padding: 2,
	whiteSpace: 'pre-wrap',
	wordBreak: 'break-word',
}

function DiagnosticItem({ diagnostic }) {
	const severity = diagnostic.severity || 'error'
	return (
		<Alert severity={severity === 'warning' ? 'warning' : severity === 'info' ? 'info' : 'error'}>
			<Stack spacing={0.5}>
				<Typography component="div" variant="body2" fontWeight={700}>
					{diagnostic.code}
					{diagnostic.targetField ? ` · ${diagnostic.targetField}` : ''}
				</Typography>
				<Typography component="div" variant="body2">
					{diagnostic.message}
				</Typography>
				{diagnostic.details !== undefined && (
					<Box component="pre" sx={{ ...codeBlockSx, maxHeight: 160, padding: 1 }}>
						{prettyPreviewValue(diagnostic.details)}
					</Box>
				)}
			</Stack>
		</Alert>
	)
}

function ContextValue({ label, value }) {
	return (
		<Box>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="body2">{value ?? '—'}</Typography>
		</Box>
	)
}

function AvitoPreviewDialog({ open, onClose, objectId }) {
	const [result, setResult] = useState(null)
	const [loading, setLoading] = useState(false)
	const [requestError, setRequestError] = useState('')
	const [classifiedAccountId, setClassifiedAccountId] = useState('')
	const [propertyRights, setPropertyRights] = useState('')
	const [accountFormOpen, setAccountFormOpen] = useState(false)
	const [recentAccounts, setRecentAccounts] = useState([])

	const refresh = useCallback(async () => {
		if (!objectId) return
		setLoading(true)
		setRequestError('')
		try {
			const response = await apiClient.get(`/classifieds/avito/preview/${objectId}`, {
				params: buildAvitoPreviewParams({ classifiedAccountId, propertyRights }),
			})
			setResult(response.data)
		} catch (error) {
			if (error.response?.data?.diagnostics) {
				setResult(error.response.data)
			} else {
				setRequestError('Не удалось получить предпросмотр. Проверьте соединение с сервером и повторите попытку.')
			}
		} finally {
			setLoading(false)
		}
	}, [classifiedAccountId, objectId, propertyRights])

	useEffect(() => {
		if (open) refresh()
	}, [open, refresh])

	const candidates = useMemo(
		() =>
			mergePreviewAccountCandidates(
				result?.publicationContext?.candidates,
				recentAccounts
			),
		[result?.publicationContext?.candidates, recentAccounts]
	)
	const diagnosticGroups = useMemo(
		() => groupPreviewDiagnostics(result?.diagnostics),
		[result?.diagnostics]
	)

	const handleAccountCreated = (account) => {
		const selection = buildCreatedAccountPreviewSelection(account, propertyRights)
		setRecentAccounts((current) => [...current, account])
		setClassifiedAccountId(selection.classifiedAccountId)
		setPropertyRights(selection.propertyRights)
		setAccountFormOpen(false)
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>Предпросмотр публикации на Авито</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={3}>
					<Alert severity="info">
						Предпросмотр доступен только для чтения. Он не публикует и не загружает объявления, не планирует выгрузку и не сохраняет её настройки.
					</Alert>

					{requestError && <Alert severity="error">{requestError}</Alert>}

					{loading && !result ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
							<CircularProgress />
						</Box>
					) : (
						<>
							<Stack
								direction={{ xs: 'column', sm: 'row' }}
								spacing={2}
								alignItems={{ xs: 'flex-start', sm: 'center' }}>
								<Chip
									label={result?.readyForPublication ? 'Готово к публикации' : 'Не готово к публикации'}
									color={result?.readyForPublication ? 'success' : 'error'}
								/>
								<Box>
									<Typography variant="overline">Категория</Typography>
									<Typography variant="body1">
										{result?.category?.categoryKey || 'Не определена'}
									</Typography>
								</Box>
							</Stack>

							<Divider />

							<Box>
								<Typography variant="h6" gutterBottom>
									Контекст публикации
								</Typography>
								<Stack
									direction={{ xs: 'column', sm: 'row' }}
									spacing={1}
									alignItems={{ xs: 'flex-start', sm: 'center' }}
									sx={{ mb: 2 }}>
									<Button variant="outlined" onClick={() => setAccountFormOpen(true)}>
										Добавить аккаунт
									</Button>
									<Tooltip title="Аккаунт определяет кабинет Авито и контактный контекст выгрузки. API Авито для файловой автозагрузки не используется.">
										<IconButton size="small" aria-label="Справка об аккаунте выгрузки">
											<HelpOutlineIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</Stack>
								{candidates.length === 0 && (
									<Alert severity="info" sx={{ mb: 2 }}>
										Аккаунтов выгрузки пока нет. Добавьте аккаунт, чтобы выбрать
										контекст объявления.
									</Alert>
								)}
								<Stack
									direction={{ xs: 'column', md: 'row' }}
									spacing={2}
									sx={{ mb: 2 }}>
									<FormControl fullWidth>
										<InputLabel id="avito-account-label">Аккаунт выгрузки</InputLabel>
										<Select
											labelId="avito-account-label"
											label="Аккаунт выгрузки"
											value={classifiedAccountId}
											disabled={candidates.length === 0}
											onChange={(event) => setClassifiedAccountId(event.target.value)}>
											<MenuItem value="">
												<em>Автоматически, если аккаунт один</em>
											</MenuItem>
											{candidates.map((candidate) => (
												<MenuItem
													key={candidate.classifiedAccountId}
													value={candidate.classifiedAccountId}>
													{candidate.accountName || 'Аккаунт Авито'}
													{candidate.accountStatus === 'disabled'
														? ' · отключён'
														: ''}
												</MenuItem>
											))}
										</Select>
									</FormControl>
									<FormControl fullWidth>
										<InputLabel id="property-rights-label">Права на объект</InputLabel>
										<Select
											labelId="property-rights-label"
											label="Права на объект"
											value={propertyRights}
											onChange={(event) => setPropertyRights(event.target.value)}>
											<MenuItem value="">
											<em>Не выбрано</em>
											</MenuItem>
											{AVITO_PROPERTY_RIGHTS.map((value) => (
												<MenuItem key={value} value={value}>
													{value}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Stack>
								<Box
									sx={{
										display: 'grid',
										gap: 2,
										gridTemplateColumns: {
											xs: '1fr',
											sm: 'repeat(2, minmax(0, 1fr))',
											md: 'repeat(4, minmax(0, 1fr))',
										},
									}}>
									<ContextValue
										label="Аккаунт"
										value={result?.publicationContext?.accountName}
									/>
									<ContextValue
										label="ID фида"
										value={result?.publicationContext?.feedId}
									/>
									<ContextValue
										label="Права на объект"
										value={result?.publicationContext?.propertyRights}
									/>
									<ContextValue
										label="Целевое состояние"
										value={result?.publicationContext?.desiredState}
									/>
								</Box>
							</Box>

							<Divider />

							<Box>
								<Typography variant="h6" gutterBottom>
									Диагностика
								</Typography>
								{diagnosticGroups.length === 0 ? (
									<Alert severity="success">Диагностических сообщений нет</Alert>
								) : (
									<Stack spacing={2}>
										{diagnosticGroups.map((group) => (
											<Box key={group.source}>
												<Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'uppercase' }}>
													{group.source}
												</Typography>
												<Stack spacing={1}>
													{group.items.map((diagnostic, index) => (
														<DiagnosticItem
															key={`${diagnostic.code}-${diagnostic.targetField || 'none'}-${index}`}
															diagnostic={diagnostic}
														/>
													))}
												</Stack>
											</Box>
										))}
									</Stack>
								)}
							</Box>

							<Divider />

							<Box>
								<Typography variant="h6" gutterBottom>
									Нормализованная запись
								</Typography>
								<Box component="pre" sx={codeBlockSx}>
									{prettyPreviewValue(result?.normalizedRecord)}
								</Box>
							</Box>

							<Box>
								<Typography variant="h6" gutterBottom>
									Предпросмотр XML
								</Typography>
								<Box component="pre" sx={codeBlockSx}>
									{result?.xml || 'XML не сформирован.'}
								</Box>
							</Box>
						</>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Закрыть</Button>
				<Button variant="contained" onClick={refresh} disabled={loading}>
					{loading ? 'Обновление…' : 'Обновить'}
				</Button>
			</DialogActions>
			<AvitoAccountFormDialog
				open={accountFormOpen}
				onClose={() => setAccountFormOpen(false)}
				onSaved={handleAccountCreated}
			/>
		</Dialog>
	)
}

export default AvitoPreviewDialog
